import React from "react";
import {Box, Button, Card, CardContent, Chip, FormControlLabel, IconButton, Paper, Switch,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, useMediaQuery} from "@mui/material";
import {Delete as DeleteIcon, Edit as EditIcon, Visibility as VisibilityIcon} from "@mui/icons-material";
import {couponLifecycle, formatCouponDate, formatCouponDiscount} from "./coupon-lifecycle";

function CouponCode({coupon}) {
    return <Box sx={{minWidth: 0, overflowWrap: "anywhere"}}><Typography fontWeight={600}>{coupon.couponCode}</Typography>
        {coupon.description && <Typography variant="body2" color="text.secondary" sx={{maxWidth: 300}}>{coupon.description}</Typography>}</Box>;
}

function CouponDiscount({coupon, language, t}) {
    return <Box><Typography fontWeight={600}>{formatCouponDiscount(coupon.discountValue, language, t("DistributorMetricUnavailable"))}</Typography>
        <Typography variant="caption" color="text.secondary">{t("CouponPercentageDiscount")}</Typography></Box>;
}

function CouponPeriod({coupon, language, t}) {
    const date = value => formatCouponDate(value, language, t("DistributorMetricUnavailable"));
    return <Box sx={{minWidth: 0}}><Typography variant="body2" title={coupon.startDate || undefined}>
        {coupon.startDate == null ? t("CouponNoStartDate") : `${t("StartDate")}: ${date(coupon.startDate)}`}</Typography>
        <Typography variant="body2" color="text.secondary" title={coupon.endDate || undefined}>
            {coupon.endDate == null ? t("NoEndDate") : `${t("EndDate")}: ${date(coupon.endDate)}`}</Typography></Box>;
}

function CouponStatus({coupon, now, t}) {
    const status = couponLifecycle(coupon, now);
    const colors = {ACTIVE: "success", SCHEDULED: "info", EXPIRED: "warning", DISABLED: "default", UNAVAILABLE: "default"};
    return <Chip size="small" variant="outlined" color={colors[status]} label={t(`CouponLifecycle_${status}`)}/>;
}

function CouponEnablement({coupon, busy, onToggle, t}) {
    const label = coupon.active === true ? "CouponEnabled" : coupon.active === false ? "CouponDisabled" : "DistributorMetricUnavailable";
    return <FormControlLabel sx={{m: 0}} label={<Typography variant="body2">{t(label)}</Typography>}
        control={<Switch checked={coupon.active === true} disabled={!!busy} onChange={() => onToggle(coupon)}
            inputProps={{"aria-label": `${t("CouponToggle")} ${coupon.couponCode}`}}/>}/>;
}

function CouponRecipients({coupon, onRecipients, t}) {
    return <Button size="small" startIcon={<VisibilityIcon/>} aria-label={`${t("Pharmacies")} ${coupon.couponCode}`}
        onClick={() => onRecipients(coupon)} sx={{textTransform: "none", minHeight: 40}}>
        {t("CouponViewPharmacies")} ({coupon.pharmacies.length})</Button>;
}

function CouponActions({coupon, busy, onEdit, onDelete, t, mobile}) {
    return <Box sx={{display: "flex", flexWrap: "wrap", gap: 1}}>
        {[{label: "EditCoupon", icon: <EditIcon/>, color: "primary", action: onEdit},
            {label: "Delete", icon: <DeleteIcon/>, color: "error", action: onDelete}].map(item => mobile
            ? <Button key={item.label} size="small" color={item.color} disabled={!!busy} startIcon={item.icon} sx={{minHeight: 40}}
                aria-label={`${t(item.label)} ${coupon.couponCode}`} onClick={() => item.action(coupon)}>{t(item.label)}</Button>
            : <Tooltip key={item.label} title={t(item.label)}><span><IconButton disabled={!!busy} color={item.color}
                aria-label={`${t(item.label)} ${coupon.couponCode}`} onClick={() => item.action(coupon)}>{item.icon}</IconButton></span></Tooltip>)}
    </Box>;
}

function CouponCard(props) {
    const {coupon, t} = props;
    return <Card component="article" variant="outlined" aria-label={coupon.couponCode} sx={{minWidth: 0}}><CardContent>
        <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5, mb: 2}}>
            <CouponCode coupon={coupon}/><CouponStatus {...props}/></Box>
        <Box sx={{display: "flex", flexDirection: "column", gap: 1.5}}><CouponDiscount {...props}/><CouponPeriod {...props}/>
            <CouponRecipients {...props}/>
            <Box><Typography variant="caption" color="text.secondary">{t("CouponEnablement")}</Typography><CouponEnablement {...props}/></Box>
            <CouponActions {...props} mobile/>
        </Box></CardContent></Card>;
}

export default function CouponCampaignList({coupons, ...props}) {
    const desktop = useMediaQuery("(min-width:900px)");
    const {t} = props;
    if (!desktop) return <Box sx={{display: "grid", gridTemplateColumns: {xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))"}, gap: 2}}>
        {coupons.map(coupon => <CouponCard key={coupon.couponId} {...props} coupon={coupon}/>)}</Box>;
    return <TableContainer component={Paper} tabIndex={0} role="region" aria-label={t("WorkspaceCouponsTable")}
        sx={{border: 1, borderColor: "divider", borderRadius: 2, maxWidth: "100%"}}>
        <Table aria-label={t("Coupons")} sx={{minWidth: 1180}}><TableHead><TableRow>
            {["CouponCode", "Discount", "ValidPeriod", "Pharmacies", "CouponLifecycle", "CouponEnablement", "Actions"].map(label =>
                <TableCell key={label} sx={{fontWeight: 600}}>{t(label)}</TableCell>)}
        </TableRow></TableHead><TableBody>{coupons.map(coupon => {
            const row = {...props, coupon};
            return <TableRow key={coupon.couponId} hover>
                <TableCell sx={{minWidth: 160}}><CouponCode coupon={coupon}/></TableCell><TableCell><CouponDiscount {...row}/></TableCell>
                <TableCell sx={{minWidth: 220}}><CouponPeriod {...row}/></TableCell><TableCell><CouponRecipients {...row}/></TableCell>
                <TableCell><CouponStatus {...row}/></TableCell><TableCell><CouponEnablement {...row}/></TableCell>
                <TableCell><CouponActions {...row}/></TableCell>
            </TableRow>;
        })}</TableBody></Table></TableContainer>;
}
